import React, { useMemo, ReactElement } from 'react';
import CheckAccess from '../../../../components/check-access/check-access.component';
import Loader from '../../../../components/loader/loader.component';
import Tooltip from '../../../../components/tooltip/tooltip.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import TableRowForm from '../../../../components/table-row-form/table-row-form.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import VisibleCell from '../../../../components/visible-cell/visible-cell.component';
import IconBlockError from '../../../../components/icon-block-error/icon-block-error.component';
import IconBlockSuccess from '../../../../components/icon-block-success/icon-block-success.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldDate from '../../../../components/form-field-date/form-field-date.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import RemoveIcon from '../../../../icons/remove-icon';
import SortService from '../../../../services/sort-service';
import FormService from '../../../../services/form-service';
import useShiftState from '../../../../hooks/use-shift-state';
import useCurrentUserPermissions from '../../../../hooks/use-current-user-permissions';
import { DEAL_TASK_ACCESS_LEVEL_VIEW } from '../../../../constants/entity/permissions';
import { SORT_TYPE_ASC } from '../../../../constants/system/sort-types';
import PersonDefaultImage from '../../../../static/images/person-default-image.png';

const DealTaskRowForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isActive,
    isSaving,
    isInlineSaving,
    isNewEntity,
    withSorting,
    dealTaskData,
    dealTaskGroupData,
    dealTaskStatusList,
    dealTaskList,
    dealTaskContactPersonList,
    accessLevelList,
    onDeleteClick,
    dealTaskNewItems,
    dealTaskIndex,
    inputRefs,
    onInlineSave,
    onInlineCancel,
    onDealTaskContactPersonSearch,
    onDealTaskContactPersonSearchClose,
    isSaveDisabled,
  } = props;

  const isShiftPressed: boolean = useShiftState();
  const permissionList: string[] = useCurrentUserPermissions();

  const dealTaskAccessLevelViewPermissions: string[] = useMemo((): string[] => [DEAL_TASK_ACCESS_LEVEL_VIEW], []);

  const orderedDealTaskList: DealTask[] = useMemo((): DealTask[] => (
    withSorting ? SortService.sort(dealTaskList || [], { field: 'name', type: SORT_TYPE_ASC }) : dealTaskList
  ), [dealTaskList, withSorting]);
  const filteredDealTaskList: DealTask[] = useMemo((): DealTask[] => (
    orderedDealTaskList && orderedDealTaskList.filter((dealTaskItem: DealTask): boolean => (
      ((dealTaskData && dealTaskItem.id !== dealTaskData.id) || !dealTaskData) && !dealTaskItem.parentId
    ))
  ), [orderedDealTaskList, dealTaskData]);

  const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupData.id);
  const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTaskData.id);
  const formPrefix: string = `dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}]`;

  const menuItemFilteredDealTaskList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: filteredDealTaskList,
  }), [filteredDealTaskList]);

  const menuItemDealTaskStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: dealTaskStatusList,
  }), [dealTaskStatusList]);

  const menuItemDealTaskContactPersonList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionItemExtraLabelKey: 'email',
    optionList: dealTaskContactPersonList,
    defaultImage: PersonDefaultImage,
  }), [dealTaskContactPersonList]);

  const menuItemAccessLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: accessLevelList,
  }), [accessLevelList]);

  const tableDealTaskListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'name',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell isSticky isStickyLeft>
          <FormFieldText isHidden name={`${formPrefix}.id`} />
          <FormFieldText isHidden name={`${formPrefix}.groupId`} />
          <FormFieldText
            name={`${formPrefix}.name`}
            placeholder="Name"
            inputRefs={inputRefs}
            inputRefIndex={`${dealTaskIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 2,
      name: 'parentId',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldSelect
            withBlankElement
            isDisabled={filteredDealTaskList && !filteredDealTaskList.length}
            isReadOnly={isShiftPressed}
            name={`${formPrefix}.parentId`}
            placeholder="Parent Task"
            optionsTotal={filteredDealTaskList && filteredDealTaskList.length}
            inputRefs={inputRefs}
            inputRefIndex={`${dealTaskIndex}.${fieldIndex}`}
          >
            {menuItemFilteredDealTaskList}
          </FormFieldSelect>
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'statusText',
      component: (field: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isNewEntity || (dealTaskStatusList && !dealTaskStatusList.length);

        return (
          <TableCell>
            <FormFieldSelect
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.status`}
              placeholder="Status"
              optionsTotal={dealTaskStatusList && dealTaskStatusList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealTaskIndex}.${fieldIndex}` : null}
            >
              {menuItemDealTaskStatusList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 4,
      name: 'dueDate',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            isReadOnly={isShiftPressed}
            name={`${formPrefix}.dueDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealTaskIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'contact.fullName',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldSelect
            withSearch
            withBlankElement
            isReadOnly={isShiftPressed}
            blankElementLabel="Unassigned"
            name={`${formPrefix}.contactId`}
            optionsTotal={dealTaskContactPersonList && dealTaskContactPersonList.length}
            inputRefs={inputRefs}
            inputRefIndex={`${dealTaskIndex}.${fieldIndex}`}
            onSearch={onDealTaskContactPersonSearch}
            onClose={onDealTaskContactPersonSearchClose}
          >
            {menuItemDealTaskContactPersonList}
          </FormFieldSelect>
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'createdDate',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {dealTaskData.createdDate}
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'documents',
      component: (): ReactElement => (
        <TableCell />
      ),
    },
    {
      id: 8,
      name: 'updates',
      component: (): ReactElement => (
        <TableCell />
      ),
    },
    {
      id: 9,
      name: 'accessLevel.title',
      permission: DEAL_TASK_ACCESS_LEVEL_VIEW,
      component: (field: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = accessLevelList && !accessLevelList.length;

        return (
          <TableCell>
            <CheckAccess permissions={dealTaskAccessLevelViewPermissions}>
              <FormFieldSelect
                withBlankElement
                isDisabled={isDisabled}
                isReadOnly={isShiftPressed}
                blankElementLabel="Everyone"
                name={`${formPrefix}.accessLevel`}
                optionsTotal={accessLevelList && accessLevelList.length}
                inputRefs={inputRefs}
                inputRefIndex={!isDisabled ? `${dealTaskIndex}.${fieldIndex}` : null}
              >
                {menuItemAccessLevelList}
              </FormFieldSelect>
            </CheckAccess>
          </TableCell>
        );
      },
    },
    {
      id: 10,
      name: 'actions',
      component: (): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight>
          {
            !isNewEntity && isSaving && isInlineSaving && (
              <Loader isInline isSmall />
            )
          }
          {
            !isNewEntity && !isSaveDisabled && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineSave(dealTaskGroupData.id, dealTaskData.id);
                }}
              >
                <IconBlockSuccess />
              </ActionButton>
            )
          }
          {
            !isNewEntity && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineCancel(dealTaskGroupData.id, dealTaskData.id);
                }}
              >
                <IconBlockError isDisabled={isSaveDisabled} />
              </ActionButton>
            )
          }
          {
            !isSaving && isNewEntity && (
              <Tooltip title="Delete">
                <ActionButton isInline withSmallPadding onClick={onDeleteClick}>
                  <RemoveIcon />
                </ActionButton>
              </Tooltip>
            )
          }
        </TableCell>
      ),
    },
  ], [isShiftPressed, isSaving, isSaveDisabled, dealTaskNewItems, dealTaskContactPersonList]);

  return (
    <TableRowForm isActive={isActive}>
      {
        tableDealTaskListFields && !!tableDealTaskListFields.length
        && tableDealTaskListFields.map((tableField: TableField, fieldIndex: number): ReactElement | boolean => (
          <VisibleCell key={tableField.id} tableField={tableField} permissionList={permissionList}>
            {!!tableField.component && tableField.component(tableField, fieldIndex)}
          </VisibleCell>
        ))
      }
    </TableRowForm>
  );
});

export default DealTaskRowForm;
